import { Button, EmptyState, Page, Spinner } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { RequireBand } from "../../app/RequireBand.js";
import { useShows } from "../../app/hooks/useShows.js";
import { ApiError } from "../../app/hooks/apiError.js";
import styles from "./MobileShowsScreen.module.scss";

type ShowSummary = components["schemas"]["ShowSummary"];

export interface MobileShowsScreenProps {
  /** Open a confirmed show's door check-in (the guest list) — `/shows/:id/guests`. */
  onOpenGuests: (showId: string) => void;
  /** Open a confirmed show's logistics — `/shows/:id/logistics`. */
  onOpenLogistics: (showId: string) => void;
}

/**
 * `/shows` — the mobile READ-ONLY shows list (ART-153 pass two). Spec §3: on mobile "Shows/Schedule are
 * read + logistics — mobile books nothing." Both the booking `shows` tab and the TM's leading `guest_list`
 * tab route here instead of the desktop `/booking` pipeline (kanban + deal modals + create), which they used
 * to dump every mobile seat on. It is MONEY-FREE by construction: `ShowSummary` carries no deal/settlement
 * figures, and nothing here creates or edits a show. A confirmed show's row opens its per-show road surfaces
 * — the door check-in (the guest list) and logistics — the "door opens per-show from the shows list" the nav
 * map anticipated. Navigation rides callbacks (the `DayOfShowScreen` pattern) so the shell owns routing.
 */
export function MobileShowsScreen({ onOpenGuests, onOpenLogistics }: MobileShowsScreenProps) {
  return (
    <RequireBand>
      <ShowsInner onOpenGuests={onOpenGuests} onOpenLogistics={onOpenLogistics} />
    </RequireBand>
  );
}

function isConfirmed(status: string | null | undefined): boolean {
  return status === "confirmed" || status === "settled";
}

function fmtDate(date: string | undefined): string {
  if (!date) return "TBD";
  // A calendar date, rendered at noon so a timezone west of UTC never rolls it back a day.
  const d = new Date(`${date}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function locationLine(s: ShowSummary): string {
  return [s.city, s.state].filter(Boolean).join(", ");
}

function statusLabel(status: string | null | undefined): string {
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function ShowsInner({ onOpenGuests, onOpenLogistics }: MobileShowsScreenProps) {
  // One read, sorted ascending by date, partitioned client-side. Money-free: no deal fields are requested or
  // shown. `sort: "date"` gives upcoming-first; Past is re-sorted newest-first below.
  const q = useShows({ sort: "date" });

  if (q.isLoading) {
    return (
      <Page title="Shows">
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={22} label="Loading shows" />
        </div>
      </Page>
    );
  }
  if (q.isError) {
    return (
      <Page title="Shows">
        <EmptyState
          title="Couldn't load shows"
          description={q.error instanceof ApiError ? q.error.message : "Check your connection and try again."}
          action={<Button onClick={() => void q.refetch()}>Retry</Button>}
        />
      </Page>
    );
  }

  const items = q.data?.items ?? [];
  // Local wall clock, not UTC — a show `date` is a calendar date, and comparing it against UTC's today rolls
  // the boundary a day early west of Greenwich (the `daysOut` fixture-date convention, same reasoning).
  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const upcoming = items.filter((s) => (s.date ?? "") >= todayIso);
  const past = items.filter((s) => (s.date ?? "") < todayIso).sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));

  if (items.length === 0) {
    return (
      <Page title="Shows">
        <EmptyState
          title="No shows yet"
          description="Confirmed shows and holds appear here. Booking a show happens on desktop."
        />
      </Page>
    );
  }

  return (
    <Page title="Shows">
      {upcoming.length > 0 && (
        <ShowSection
          heading="Upcoming"
          shows={upcoming}
          actionable
          onOpenGuests={onOpenGuests}
          onOpenLogistics={onOpenLogistics}
        />
      )}
      {past.length > 0 && (
        <ShowSection
          heading="Past"
          shows={past}
          actionable={false}
          onOpenGuests={onOpenGuests}
          onOpenLogistics={onOpenLogistics}
        />
      )}
    </Page>
  );
}

function ShowSection({
  heading,
  shows,
  actionable,
  onOpenGuests,
  onOpenLogistics,
}: { heading: string; shows: ShowSummary[]; actionable: boolean } & MobileShowsScreenProps) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHead}>{heading}</h2>
      <ul className={styles.list}>
        {shows.map((s) => (
          <li key={s.showId} className={styles.row}>
            <div className={styles.rowMain}>
              <span className={styles.date}>{fmtDate(s.date)}</span>
              <span className={styles.venue}>{s.venueName || "Venue TBD"}</span>
              {locationLine(s) && <span className={styles.loc}>{locationLine(s)}</span>}
            </div>
            <span className={styles.status} data-status={s.status ?? undefined}>
              {statusLabel(s.status)}
            </span>
            {/* Door check-in + logistics are for shows still to come; a past show is a read-only record. */}
            {actionable && isConfirmed(s.status) && s.showId && (
              <div className={styles.actions}>
                <button type="button" className={styles.action} onClick={() => onOpenGuests(s.showId!)}>
                  Guest list
                </button>
                <button type="button" className={styles.action} onClick={() => onOpenLogistics(s.showId!)}>
                  Logistics
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
